import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Cliente del módulo Rail Perú del backend (`/peru`) — espejo de `ColombiaService`
 * acotado a USD→PEN (rail único `PE`, moneda con 2 decimales). Token Cognito lo
 * agrega ApiRestInterceptor. NO importa nada de Colombia.
 */

export type PeruRail = 'PE';
export const PERU_RAILS: PeruRail[] = ['PE'];

export interface RailConfigPe {
  id: number;
  pais: string;
  rail: PeruRail;
  comisionPct: number | string;
  fijoUsd: number | string;
  /** Costo fijo en PEN (columna compartida `costo_fijo_cop`). */
  costoFijoCop: number | string;
  modoPricing?: 'A' | 'ESPEJO';
  margenMinUsd?: number | string;
  modoTasa: 'AUTO' | 'MANUAL';
  tasaCostoManual: number | string | null;
  tasaManualVigenteHasta: string | null;
  motivo: string | null;
  responsible: string;
  vigenteDesde: string;
  vigenteHasta: string | null;
}

export interface UpsertRailConfigPe {
  rail: PeruRail;
  comisionPct: number;
  fijoUsd: number;
  /** Costo fijo interno en PEN. */
  costoFijoLocal: number;
  modoPricing?: 'A' | 'ESPEJO';
  margenMinUsd?: number;
  modoTasa: 'AUTO' | 'MANUAL';
  tasaCostoManual?: number;
  tasaManualVigenteHasta?: string;
  motivo?: string;
}

export interface LiveRatesPe {
  rails: Record<
    PeruRail,
    {
      partnerUsdSell: number;
      publicUsdSell: number | null;
      publicSellUsd?: number | null;
      /** Fee retail de Vita en PEN. */
      publicFeeLocal?: number | null;
      fixedCostLocal: number;
      businessDays: number;
      validUntil: string | null;
    } | null
  >;
  publicaStale: boolean;
  fetchedAt: string;
}

export interface QuotePreviewRequestPe {
  montos: number[];
  modoPricing: 'A' | 'ESPEJO';
  comisionPct?: number;
  fijoUsd?: number;
  margenMinUsd?: number;
  costoFijoLocal?: number;
}

export interface PricingResultPe {
  montoUsd: number;
  tasaPublica: number;
  tasaPartner: number;
  clamped?: boolean;
  /** ESPEJO: lo que Vita pública entregaría (PEN). */
  ofertaVitaLocal?: number;
  spreadPct: number;
  comisionAguacateUsd: number;
  markupEfectivoPct: number;
  feeVitaLocal: number;
  /** Lo que recibe el destinatario, en PEN (2 decimales). */
  recibe: number;
  tasaEfectivaCliente: number;
  costoUsd: number;
  gananciaUsd: number;
}

export interface QuoteResultPe {
  disponible: boolean;
  motivo?: string;
  rail: PeruRail;
  paisActivo?: boolean;
  tasaSource?: 'AUTO' | 'MANUAL' | 'MANUAL_EXPIRED';
  modoPricing?: 'A' | 'ESPEJO';
  espejoFallback?: boolean;
  stale?: boolean;
  publicaFallback?: boolean;
  margenOk?: boolean;
  businessDays?: number;
  validUntil?: string | null;
  usdSellVivo?: number | null;
  resultado?: PricingResultPe;
}

export interface QuotePreviewItemPe {
  montoUsd: number;
  /** Lo que Vita retail entregaría HOY por este monto en PEN (null si feed caído). */
  ofertaVitaLocal: number | null;
  quote: QuoteResultPe;
}

export interface QuotePreviewPe {
  paisActivo: boolean;
  fetchedAt: string;
  publicaStale: boolean;
  rails: Record<PeruRail, { configurado: boolean; items: QuotePreviewItemPe[] }>;
}

/** Switchboard de rieles activos hacia PEN (parametros RAILES_ACTIVOS_PE). */
export interface RailesActivosPe {
  pen: { usdt_pen: boolean; ves_pen: boolean; clp_pen: boolean };
}

// ─── VES→PEN (pata VES→USD; config propia pais='PE', independiente de VES→COP) ───

export type VesPenTasaSource = 'AUTO' | 'MANUAL';

export interface VesPenConfig {
  id: number;
  pais: string;
  tasaSource: VesPenTasaSource;
  tasaManual: number | string | null;
  tasaManualVigenteHasta: string | null;
  markupPct: number | string;
  minVes: number | string;
  motivo: string | null;
  responsible: string;
  vigenteDesde: string;
  vigenteHasta: string | null;
}

export interface UpsertVesPenConfig {
  tasaSource: VesPenTasaSource;
  tasaManual?: number;
  tasaManualVigenteHasta?: string;
  markupPct?: number;
  minVes?: number;
  motivo?: string;
}

export interface VesPenUsdRate {
  base: number;
  efectiva: number;
  source: VesPenTasaSource;
  stale: boolean;
  markupPct: number;
}

export interface VesPenLiveRates {
  liveVes: number | null;
  rate: VesPenUsdRate | null;
  activo: boolean;
  minVes: number;
  fetchedAt: string;
}

export interface VesPenPreviewItem {
  montoVes: number;
  montoUsd: number;
  cumpleMinimo: boolean;
  recibePen: number;
  /** Tasa única que ve el cliente: PEN por VES. */
  tasaEfectivaPenVes: number;
  gananciaUsd: number;
  disponible: boolean;
  margenOk: boolean;
  ofertaVitaPen: number | null;
  tasaVitaPenVes: number | null;
  clamped: boolean;
  espejoFallback: boolean;
  stale: boolean;
}

export interface VesPenPreview {
  activo: boolean;
  rate: VesPenUsdRate | null;
  minVes: number;
  fetchedAt: string;
  rails: Record<PeruRail, VesPenPreviewItem[]>;
}

export interface VesPenPreviewOverride {
  tasaSource?: VesPenTasaSource;
  tasaManual?: number;
  tasaManualVigenteHasta?: string;
  markupPct?: number;
  minVes?: number;
}

// ─── CLP→PEN (pata CLP→USD; config propia pais='PE', independiente de CLP→COP) ───

export type ClpPenTasaSource = 'LIVE_SELL' | 'SPOT' | 'MANUAL';

export interface ClpPenConfig {
  id: number;
  pais: string;
  tasaSource: ClpPenTasaSource;
  tasaManual: number | string | null;
  tasaManualVigenteHasta: string | null;
  markupPct: number | string;
  minClp: number | string;
  motivo: string | null;
  responsible: string;
  vigenteDesde: string;
  vigenteHasta: string | null;
}

export interface UpsertClpPenConfig {
  tasaSource: ClpPenTasaSource;
  tasaManual?: number;
  tasaManualVigenteHasta?: string;
  markupPct?: number;
  minClp?: number;
  motivo?: string;
}

export interface ClpPenUsdRate {
  base: number;
  efectiva: number;
  source: ClpPenTasaSource;
  stale: boolean;
  markupPct: number;
}

export interface ClpPenLiveRates {
  spot: number | null;
  liveSell: number | null;
  rate: ClpPenUsdRate | null;
  activo: boolean;
  minClp: number;
  fetchedAt: string;
}

export interface ClpPenPreviewItem {
  montoClp: number;
  montoUsd: number;
  cumpleMinimo: boolean;
  recibePen: number;
  /** Tasa única que ve el cliente: PEN por CLP. */
  tasaEfectivaPenClp: number;
  gananciaUsd: number;
  disponible: boolean;
  margenOk: boolean;
  ofertaVitaPen: number | null;
  tasaVitaPenClp: number | null;
  clamped: boolean;
  espejoFallback: boolean;
  stale: boolean;
}

export interface ClpPenPreview {
  activo: boolean;
  rate: ClpPenUsdRate | null;
  minClp: number;
  fetchedAt: string;
  rails: Record<PeruRail, ClpPenPreviewItem[]>;
}

export interface ClpPenPreviewOverride {
  tasaSource?: ClpPenTasaSource;
  tasaManual?: number;
  tasaManualVigenteHasta?: string;
  markupPct?: number;
  minClp?: number;
}

@Injectable({ providedIn: 'root' })
export class PeruService {
  private readonly API = environment.apiNest;

  constructor(private http: HttpClient) {}

  getConfig(): Promise<Record<PeruRail, RailConfigPe | null>> {
    return firstValueFrom(
      this.http.get<Record<PeruRail, RailConfigPe | null>>(`${this.API}peru/config`),
    );
  }

  getConfigHistory(rail: PeruRail = 'PE'): Promise<RailConfigPe[]> {
    return firstValueFrom(
      this.http.get<RailConfigPe[]>(`${this.API}peru/config/history?rail=${rail}`),
    );
  }

  upsertConfig(dto: UpsertRailConfigPe): Promise<RailConfigPe> {
    return firstValueFrom(
      this.http.post<RailConfigPe>(`${this.API}peru/config`, dto),
    );
  }

  getLive(): Promise<LiveRatesPe> {
    return firstValueFrom(this.http.get<LiveRatesPe>(`${this.API}peru/live`));
  }

  quote(montoUsd: number, rail: PeruRail = 'PE'): Promise<QuoteResultPe> {
    return firstValueFrom(
      this.http.post<QuoteResultPe>(`${this.API}peru/quote`, { rail, montoUsd }),
    );
  }

  /** Preview de config (read-only): cotiza varios montos con la config del form. */
  quotePreview(req: QuotePreviewRequestPe): Promise<QuotePreviewPe> {
    return firstValueFrom(
      this.http.post<QuotePreviewPe>(`${this.API}peru/quote-preview`, req),
    );
  }

  getRailesActivos(): Promise<RailesActivosPe> {
    return firstValueFrom(
      this.http.get<RailesActivosPe>(`${this.API}peru/railes-activos`),
    );
  }

  setRailesActivos(patch: { usdtPen?: boolean; vesPen?: boolean; clpPen?: boolean }): Promise<RailesActivosPe> {
    return firstValueFrom(
      this.http.post<RailesActivosPe>(`${this.API}peru/railes-activos`, patch),
    );
  }

  /** Resolución manual del operador (REQUIRES_HUMAN_REVIEW). */
  resolverRemesa(
    txId: number,
    resultado: 'ENVIADA' | 'RECHAZADA' | 'REANUDAR' | 'ANULAR',
    motivo?: string,
  ): Promise<{ txId: number; resultado: string }> {
    return firstValueFrom(
      this.http.post<{ txId: number; resultado: string }>(
        `${this.API}peru/remesa-pe/${txId}/resolver`,
        { resultado, motivo },
      ),
    );
  }

  // ─── Config VES→PEN (pata VES→USD, pais='PE') ───

  getVesConfig(): Promise<VesPenConfig | null> {
    return firstValueFrom(
      this.http.get<VesPenConfig | null>(`${this.API}peru/ves/config`),
    );
  }

  getVesConfigHistory(): Promise<VesPenConfig[]> {
    return firstValueFrom(
      this.http.get<VesPenConfig[]>(`${this.API}peru/ves/config/history`),
    );
  }

  upsertVesConfig(dto: UpsertVesPenConfig): Promise<VesPenConfig> {
    return firstValueFrom(
      this.http.post<VesPenConfig>(`${this.API}peru/ves/config`, dto),
    );
  }

  getVesLive(): Promise<VesPenLiveRates> {
    return firstValueFrom(
      this.http.get<VesPenLiveRates>(`${this.API}peru/ves/live`),
    );
  }

  vesQuotePreview(
    montosVes: number[],
    override?: VesPenPreviewOverride,
  ): Promise<VesPenPreview> {
    return firstValueFrom(
      this.http.post<VesPenPreview>(`${this.API}peru/ves/quote-preview`, {
        montosVes,
        ...(override ?? {}),
      }),
    );
  }

  // ─── Config CLP→PEN (pata CLP→USD, pais='PE') ───

  getClpConfig(): Promise<ClpPenConfig | null> {
    return firstValueFrom(
      this.http.get<ClpPenConfig | null>(`${this.API}peru/clp/config`),
    );
  }

  getClpConfigHistory(): Promise<ClpPenConfig[]> {
    return firstValueFrom(
      this.http.get<ClpPenConfig[]>(`${this.API}peru/clp/config/history`),
    );
  }

  upsertClpConfig(dto: UpsertClpPenConfig): Promise<ClpPenConfig> {
    return firstValueFrom(
      this.http.post<ClpPenConfig>(`${this.API}peru/clp/config`, dto),
    );
  }

  getClpLive(): Promise<ClpPenLiveRates> {
    return firstValueFrom(
      this.http.get<ClpPenLiveRates>(`${this.API}peru/clp/live`),
    );
  }

  clpQuotePreview(
    montosClp: number[],
    override?: ClpPenPreviewOverride,
  ): Promise<ClpPenPreview> {
    return firstValueFrom(
      this.http.post<ClpPenPreview>(`${this.API}peru/clp/quote-preview`, {
        montosClp,
        ...(override ?? {}),
      }),
    );
  }
}
